import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import Header from "components/Header";
import { EyeOffOutlineSVGIcon, EyeOutlineSVGIcon, WalletSVGIcon } from "components/SVGIcons";
import TabSwitch from "components/TabSwitch";
import { BROKER_TAB_ITEMS } from "constants/haulpay-broker";
import { routes } from "constants/routes";
import { usePullToRefresh } from "context/PullToRefreshContext";
import { formatNumber } from "helpers/formatNumber";
import { showErrorMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import { AppDispatch } from "store";
import { getWalletBalance, refreshWalletBalance } from "store/home/actions";
import { selectWalletBalance } from "store/home/selector";
// import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";
import OnboardingModal from "./components/OnboardingModal";
import useStripeOnboarding, { StripeOnboarding } from "../../../../../../hooks/useStripeOnBoarding";
import { useToggle } from "usehooks-ts";
import walletOnboardingStatus from "helpers/walletOnboardingStatus";
import StripeAccountStatus from "./components/StripeAccountStatus";
import AddExternalBankAccountModal from "./components/AddExternalBankAccountModal";
import DepositMoneyWallet from "./components/DepositMoneyWallet";
import PendingBalanceExplainer from "./components/PendingBalanceExplainer";
import PendingSchedulePanel from "./components/PendingSchedulePanel";

export default function StripeWallet() {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const { accountSettings, statusMapping }: StripeOnboarding = useStripeOnboarding();
  const [onBoardingRequiredModal, onBoardingRequiredModalHandler] = useToggle();
  const [accountStatusModal, accountStatusModalHandler] = useToggle();
  const [openBankAccountModal, openBankAccountModalHandler] = useToggle();
  const [openDepositMoneyModel, openDepositMoneyModelHandler] = useToggle();
  const [isShow, setIsShow] = useState(true);
  const walletBalance = useSelector(selectWalletBalance);

  const fetchWalletBalance = async () => {
    await dispatch(getWalletBalance());
  };

  useEffect(() => {
    fetchWalletBalance();
  }, []);

  useEffect(() => {
    const refetchOnFocus = () => {
      fetchWalletBalance();
    };
    window.addEventListener("focus", refetchOnFocus);
    return () => window.removeEventListener("focus", refetchOnFocus);
  }, []);

  usePullToRefresh(() =>
    Promise.all([
      dispatch(refreshWalletBalance())
        .unwrap()
        .catch(() => showErrorMessage("Failed to fetch wallet balance.")),
      queryClient.invalidateQueries(["fetch_stripe_account"])
    ])
  );

  useEffect(() => {
    if (
      accountSettings?.onboarding_required &&
      accountSettings?.onboarding_status === walletOnboardingStatus.ONBOARDING_REQUIRED
    ) {
      return onBoardingRequiredModalHandler();
    }
    if (
      [
        walletOnboardingStatus.PENDING,
        walletOnboardingStatus.ONBOARDING_REQUIRED,
        walletOnboardingStatus.DELETED,
        walletOnboardingStatus.ONBOARDING_REVIEW
      ].includes(accountSettings.onboarding_status)
    ) {
      return accountStatusModalHandler();
    }
    if (accountSettings.onboarding_status === walletOnboardingStatus.EXTERNAL_ACCOUNT_REQUIRED) {
      return openBankAccountModalHandler();
    }
  }, [accountSettings]);

  const closeHandler = (refresh: boolean) => {
    openBankAccountModalHandler();
    if (refresh) {
      fetchWalletBalance();
    }
  };

  const depositCloseHandler = () => {
    openDepositMoneyModelHandler();
    fetchWalletBalance();
  };

  return (
    <MainLayout
      desktopTitle="My Wallet"
      customHeader={<Header label="My Wallet" goBackPath={routes.HAULPAY} isHideDesktop />}
      className={styles.brokerWalletContainer}>
      <div className={styles.cardWrapper}>
        <div className={styles.cardContent}>
          <div className={styles.currentBalance}>
            <span className="text-sm desktop:text-base">Current Balance</span>
            <div className="flex items-end">
              <span className="text-3xl font-bold">
                {isShow ? formatNumber(walletBalance?.balance ?? 0, 2).split(".")[0] : "$***"}
              </span>
              <span className="text-xl">{`.${isShow ? formatNumber(walletBalance?.balance ?? 0, 2).split(".")[1] : "**"}`}</span>
              <button onClick={() => setIsShow(!isShow)}>
                {isShow ? (
                  <EyeOffOutlineSVGIcon className="ml-3.5 mb-1.5" />
                ) : (
                  <EyeOutlineSVGIcon className="ml-3.5 mb-2" />
                )}
              </button>
            </div>
          </div>

          <div className={styles.description}>
            <div className={styles.item}>
              <span>Total Pending Invoices</span>
              <span className="text-lg font-medium">
                {walletBalance ? walletBalance.pending_count : 0}
              </span>
            </div>

            <div className={styles.item}>
              <span className="flex items-center gap-1">
                Pending Balance
                <PendingBalanceExplainer align="right" />
              </span>
              <span className="text-lg font-medium">
                ({walletBalance ? formatNumber(walletBalance.pending ?? 0, 2) : "-"})
              </span>
            </div>
          </div>
          {accountSettings?.deposit_enabled && (
            <button className={styles.addMoneyBackground}>
              <span className={styles.addMoneyButton} onClick={openDepositMoneyModelHandler}>
                ADD MONEY
              </span>
            </button>
          )}
        </div>

        <div className={styles.pendingBalance}>
          <span className="flex items-center justify-end gap-1 text-sm">
            Pending
            <PendingBalanceExplainer align="right" />
          </span>
          ({walletBalance ? formatNumber(walletBalance.pending ?? 0, 2) : "-"})
        </div>

        <WalletSVGIcon className={styles.walletIcon} />
      </div>

      {walletBalance?.pending_schedule && (
        <PendingSchedulePanel
          schedule={walletBalance.pending_schedule}
          asOf={walletBalance.pending_schedule_as_of}
        />
      )}

      <TabSwitch options={BROKER_TAB_ITEMS} />

      <Outlet />
      {onBoardingRequiredModal && (
        <OnboardingModal
          isConfirmModalOpen={onBoardingRequiredModal}
          toggleConfirmModal={onBoardingRequiredModalHandler}
        />
      )}
      {accountStatusModal && accountSettings?.onboarding_status && (
        <StripeAccountStatus
          onboardingStatus={accountSettings?.onboarding_status}
          statusMapping={statusMapping}
          open={accountStatusModal}
          closeHandler={accountStatusModalHandler}
        />
      )}
      {openBankAccountModal && accountSettings?.id && (
        <AddExternalBankAccountModal
          accountId={accountSettings?.id}
          open={openBankAccountModal}
          closeHandler={closeHandler}
        />
      )}
      <DepositMoneyWallet open={openDepositMoneyModel} closeHandler={depositCloseHandler} />
      <div className="relative flex flex-1 mt-10">
        <footer className="absolute bottom-0 w-full bg-background p-4 m-0 gap-6 px-5 pb-5 rounded-base drop-shadow-xl">
          <div className="max-w-[90%]  mx-auto w-full">
            <p className="font-semibold text-xs text-center">
              To report a persistent issue or any disputes you can email our support team{" "}
              <a className="text-info" href="mailto:support@haulpay.io">
                here
              </a>{" "}
              for assistance.
              <br />
              HaulPay partners with Stripe Payments Company for money transmission services and
              account services with funds held at Fifth Third Bank N.A., Member FDIC.
            </p>
          </div>
        </footer>
      </div>
    </MainLayout>
  );
}
